import { useState } from 'react';
import { Loader2, Printer } from 'lucide-react';
import { useMinisterReceipts, useReceipt } from '../hooks/useReceipts';
import { ReceiptPrintPreview } from './ReceiptPrintPreview';
import { receiptToPrintData, formatReceiptNumber } from '../types';
import { publicReceiptUrl } from '../utils/publicReceiptUrl';
import { formatMoney, formatShortDate } from '../utils/receiptFormat';

/** Every receipt issued for a minister: when, why and how much. */
export function MinisterReceiptsTab({ ministerId }: { ministerId: string }) {
  const { data: receipts = [], isLoading, error } = useMinisterReceipts(ministerId);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const { data: printing } = useReceipt(printingId ?? undefined);

  if (isLoading) {
    return <div className="flex h-32 items-center justify-center"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;
  }
  if (error) {
    return <p className="text-sm text-destructive">No se pudo cargar el historial de recibos.</p>;
  }
  if (receipts.length === 0) {
    return <p className="text-sm text-muted-foreground">Este ministro aún no tiene recibos emitidos.</p>;
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted/60 text-left">
              <th className="px-3 py-2 font-semibold">N°</th>
              <th className="px-3 py-2 font-semibold">Fecha</th>
              <th className="px-3 py-2 font-semibold">Motivo</th>
              <th className="px-3 py-2 text-right font-semibold">Total</th>
              <th className="px-3 py-2 text-center font-semibold">Estado</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {receipts.map((r) => (
              <tr key={r.id} className="border-t border-border/50">
                <td className="px-3 py-2 font-mono text-xs">{formatReceiptNumber(r.number)}</td>
                <td className="px-3 py-2">{formatShortDate(r.issued_at)}</td>
                <td className="px-3 py-2">{r.category?.name}</td>
                <td className="px-3 py-2 text-right font-medium">{formatMoney(r.total)} {r.currency}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    r.status === 'issued' ? 'bg-success/15 text-success' : 'bg-destructive/15 text-destructive'
                  }`}>
                    {r.status === 'issued' ? 'Emitido' : 'Anulado'}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    onClick={() => setPrintingId(r.id)}
                    aria-label={`Ver o imprimir recibo ${formatReceiptNumber(r.number)}`}
                    className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <Printer className="size-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {printing && (
        <ReceiptPrintPreview
          data={receiptToPrintData(printing, publicReceiptUrl(printing.public_token))}
          onClose={() => setPrintingId(null)}
        />
      )}
    </>
  );
}
